import React from "react";
import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { RelatedLink } from "@/components/RelatedPages";
import { buildMedicalClinicLocal, Crumb } from "@/components/schemaConstants";
import CrisisLifelineBanner from "@/components/CrisisLifelineBanner";

/**
 * /medicare-tms-coverage
 *
 * In-network claim limited to Medicare and the VA. No dollar promises; coverage
 * language stays conditional ("may"). Ends with the 988 crisis line.
 */
const faqs: FAQItem[] = [
  {
    question: "Does Medicare cover TMS?",
    answer:
      "Yes. Medicare Part B can cover TMS therapy for major depression when medical-necessity criteria are met — typically a diagnosis of major depressive disorder and prior antidepressant treatment that did not give enough relief. Neuro Wellness is an in-network Medicare provider in Coral Springs, FL, and we verify your benefits for free.",
  },
  {
    question: "Does Medicare Advantage cover TMS?",
    answer:
      "Medicare Advantage plans generally cover the same services as Original Medicare, but they may require prior authorization and may want you to see in-network providers. We handle the prior-authorization paperwork for you when we verify your benefits.",
  },
  {
    question: "Do I need a referral?",
    answer:
      "A referral is not required to book a free consultation. If you have a current psychiatrist or primary care doctor, we are happy to coordinate with them with your permission, and some Medicare Advantage plans may ask for one.",
  },
  {
    question: "How many sessions are covered?",
    answer:
      "The number of covered sessions depends on your plan and the treatment protocol your physician recommends. We confirm your covered session details during your free benefits check before you start.",
  },
  {
    question: "Does Medicare cover SPRAVATO®?",
    answer:
      "SPRAVATO® (esketamine) may be covered under Medicare when criteria for treatment-resistant depression are met. See our Medicare & SPRAVATO® coverage page for details, and we verify your benefits for free.",
  },
];

const relatedLinks: RelatedLink[] = [
  {
    title: "Depression Treatment for Seniors",
    href: "/senior-depression-treatment",
    description: "Outpatient depression care for adults 65+ in Coral Springs.",
  },
  {
    title: "TMS Therapy",
    href: "/services/tms",
    description: "Medication-free magnetic stimulation for depression.",
  },
  {
    title: "Insurance for TMS Depression Treatment",
    href: "/insurance-for-tms-depression-treatment",
    description: "How insurance verification works across carriers.",
  },
];

const breadcrumbs: Crumb[] = [
  { name: "Home", href: "/" },
  { name: "Insurance", href: "/insurance-for-tms-depression-treatment" },
  { name: "Medicare & TMS", href: "/medicare-tms-coverage" },
];

const MedicareTmsCoverage: React.FC = () => {
  return (
    <>
      <DepressionContentPage
        seo={{
          title:
            "Does Medicare Cover TMS for Depression? | Coral Springs, FL | Neuro Wellness",
          description:
            "Yes, Medicare can cover TMS therapy for depression when criteria are met. Neuro Wellness is in-network with Medicare and verifies your benefits free.",
          canonical: "/medicare-tms-coverage",
          keywords:
            "does Medicare cover TMS, Medicare TMS coverage Florida, TMS depression Medicare Coral Springs, in-network Medicare TMS provider",
          breadcrumbs,
          extraSchema: [buildMedicalClinicLocal()],
        }}
        badge="Coral Springs, FL · Medicare In-Network"
        heroTitle="Does Medicare Cover TMS Therapy for Depression?"
        heroSubtitle="Yes — Medicare Part B can cover TMS for major depression when criteria are met. We're an in-network Medicare provider and verify your benefits free."
        heroCtaPrimary="Verify My Medicare Benefits — Free"
        heroCtaPrimaryHref="/#consultation"
        reviewedBy="Daniel Pietras, MD"
        lastUpdated="2026-11-04"
        sections={[
          {
            heading: "The short answer",
            variant: "callout",
            callout:
              "Yes, Medicare Part B can cover TMS therapy for major depression when medical-necessity criteria are met, typically a diagnosis of major depressive disorder and prior antidepressant treatment that didn't give enough relief. Neuro Wellness TMS Centers of America is an in-network Medicare provider in Coral Springs, FL, and we check your coverage free.",
          },
          {
            heading: "Who usually qualifies under Medicare",
            variant: "text",
            body: (
              <>
                <p>
                  In plain terms, Medicare coverage for TMS typically requires:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>A diagnosis of major depressive disorder.</li>
                  <li>
                    Prior antidepressant trials and/or therapy that did not give
                    enough relief.
                  </li>
                  <li>
                    An evaluation by our physician confirming TMS is
                    appropriate.
                  </li>
                </ul>
                <p>
                  Exact criteria depend on your plan; we confirm them for you
                  during your free benefits check.
                </p>
              </>
            ),
          },
          {
            heading: "Original Medicare vs. Medicare Advantage",
            variant: "text",
            body: (
              <>
                <p>
                  Under Original Medicare, TMS for depression is generally
                  covered under Part B when medical-necessity criteria are met.
                  Medicare Advantage plans cover the same services but may
                  require prior authorization and may want you to see in-network
                  providers.
                </p>
                <p>
                  We handle the prior-authorization paperwork for you. When you
                  schedule a consultation, we contact your plan, confirm what is
                  covered, and explain any steps your plan requires before you
                  begin.
                </p>
              </>
            ),
          },
          {
            heading: "What you may pay",
            variant: "text",
            body: (
              <>
                <p>
                  Under Part B, after you meet your deductible, you typically
                  pay a 20% coinsurance for covered outpatient services. A
                  Medicare supplement (Medigap) plan may cover some or all of
                  that coinsurance. Exact costs depend on your plan and any
                  secondary coverage.
                </p>
                <p>
                  We keep all of this conditional — no dollar promises — and we
                  explain your specific costs after we verify your benefits.
                </p>
              </>
            ),
          },
          {
            heading: "Standard TMS vs. SAINT® under Medicare",
            variant: "text",
            body: (
              <>
                <p>
                  Standard TMS — a course of daily sessions over several weeks —
                  is the protocol Medicare most routinely covers for major
                  depression. SAINT® is an accelerated, fMRI-guided 5-day
                  protocol for adults 22 and older; coverage for accelerated
                  protocols is reviewed case by case.
                </p>
                <p>
                  At your consultation we review both options, what your plan is
                  likely to cover for each, and what makes the most sense for
                  your situation.
                </p>
              </>
            ),
          },
          {
            heading: "How our free benefits check works",
            variant: "steps",
            steps: [
              {
                title: "You give us your card",
                body: "Bring your Medicare card (and any secondary or Advantage card) to your consultation, or share the details by phone.",
              },
              {
                title: "We contact your plan",
                body: "Our team verifies your TMS benefits, any prior-authorization requirements, and your estimated out-of-pocket cost.",
              },
              {
                title: "You get a clear answer",
                body: "Before you start treatment, you receive a plain-English summary of what is covered and what you may owe — no surprises.",
              },
            ],
          },
        ]}
        faq={faqs}
        faqHeading="Frequently Asked Questions"
        relatedLinks={relatedLinks}
        relatedHeading="Related Resources"
        finalCtaHeading="Let's check your Medicare benefits — free"
        finalCtaText="Call our Coral Springs team at 954-903-1323 or book a free consultation. We verify your Medicare coverage at no cost before you begin. Eligibility is decided at the consultation; individual results vary."
        finalCtaPrimaryLabel="Verify My Medicare Benefits — Free"
        finalCtaPrimaryHref="/#consultation"
        finalCtaSecondaryLabel="Book a Free Consultation"
        finalCtaSecondaryHref="tel:9549031323"
      />
      <div className="container-px mx-auto max-w-3xl px-4 pb-12">
        <CrisisLifelineBanner />
      </div>
    </>
  );
};

export default MedicareTmsCoverage;
